import React, { useEffect, useLayoutEffect } from 'react';
import { RunPlanProvider, useRunPlan } from './context/RunPlanContext';
import { SettingsProvider } from './context/SettingsContext';
import { AnalyticsProvider } from './context/AnalyticsContext';
import Modal from './components/Modal';
import NavigationBar from './components/NavigationBar';
import TrainingPlanSelection from './components/TrainingPlanSelection';
import CalendarView from './components/CalendarView';
import AnalyticsView from './components/AnalyticsView';
import SettingsView from './components/SettingsView';
import ScoutView from './components/ScoutView';
import DuplicateWeekModal from './components/DuplicateWeekModal';
import SharedPlanPreviewModal from './components/SharedPlanPreviewModal';
import ErrorBoundary from './components/ErrorBoundary';
import useMediaQuery from './hooks/useMediaQuery';
import useLocalStorage from './hooks/useLocalStorage';
import { FILE_TYPES } from './constants';
import { getButtonClass, getThemeBackgroundColor } from './utils/styleHelpers';
import { Toaster } from 'react-hot-toast';
import WelcomeOnboarding from './components/WelcomeOnboarding';

const RunPlanApp = () => {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const {
    selectedPlan,
    darkMode,
    activeView,
    setActiveView,
    showAddRun,
    setShowAddRun,
    selectedDate,
    editingRun,
    setEditingRun,
    showDuplicateMenu,
    setShowDuplicateMenu,
    weekToDuplicate,
    setWeekToDuplicate,
    previewPlanData,
    setPreviewPlanData,
    t,
    types,
    addRun,
    deleteRun,
    duplicateWeek,
    distancePresets,
    setDistancePresets,
    restDayIcon,
    createCustomPlan,
    importCSV,
    importPlanFromURL,
    handleMakeCopy,
    handleUseAsTemplate,
  } = useRunPlan();
  const [hasSeenWelcome, setHasSeenWelcome] = useLocalStorage('hasSeenWelcome', false);
  const shouldShowWelcome = !hasSeenWelcome;

  const dismissWelcome = () => setHasSeenWelcome(true);

  const handleWelcomeExplore = () => {
    dismissWelcome();
    setActiveView('plans');
  };

  const handleWelcomeCreate = () => {
    dismissWelcome();
    createCustomPlan();
    setActiveView('calendar');
  };

  // Check for shared plan in URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const planData = params.get('plan');

    if (planData) {
      const success = importPlanFromURL(planData);

      if (success) {
        // Clean up URL after importing
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, [importPlanFromURL]);

  // Apply themed background to body across all screen sizes
  // Use useLayoutEffect to prevent flash of white background
  useLayoutEffect(() => {
    // Check if actually a mobile device (not just narrow window)
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    // Get themed background color for both light and dark mode
    const backgroundColor = getThemeBackgroundColor(t.primary, darkMode);

    if (darkMode) {
      // Dark mode: subtle pattern on desktop only
      if (!isMobileDevice) {
        // Subtle dot pattern with the themed dark color as base
        document.body.style.background = 'radial-gradient(circle, rgba(148, 163, 184, 0.1) 1px, transparent 1px)';
        document.body.style.backgroundSize = '20px 20px';
      } else {
        document.body.style.background = '';
        document.body.style.backgroundSize = '';
      }
      document.body.style.backgroundColor = backgroundColor;
    } else {
      // Light mode: themed background on all screen sizes
      if (!isMobileDevice) {
        // Desktop: clean themed color
        document.body.style.background = '';
        document.body.style.backgroundSize = '';
      } else {
        // Mobile: solid color
        document.body.style.background = '';
        document.body.style.backgroundSize = '';
      }
      document.body.style.backgroundColor = backgroundColor;
    }
  }, [darkMode, t.primary]);

  // Desktop: wider container (max-w-4xl = 896px), Mobile: max-w-md
  // Add drop shadow on desktop
  const containerClass = darkMode
    ? `${isDesktop ? 'max-w-4xl shadow-2xl' : 'max-w-md'} mx-auto min-h-screen bg-slate-950 text-white`
    : `${isDesktop ? 'max-w-4xl shadow-xl' : 'max-w-md'} mx-auto min-h-screen bg-white`;

  if (!selectedPlan && activeView === 'calendar') {
    const handleImportClick = () => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = FILE_TYPES.CSV;
      input.onchange = (e) => {
        importCSV(e);
      };
      input.click();
    };

    return (
      <div className={containerClass}>
        {/* Desktop: Navigation at top */}
        {isDesktop && <NavigationBar activeView={activeView} setActiveView={setActiveView} darkMode={darkMode} theme={t} isDesktop={isDesktop} selectedPlan={selectedPlan} />}

        <div className={'bg-gradient-to-r text-white shadow-lg relative overflow-hidden ' + t.grad}>
          {/* Subtle background pattern */}
          <div className="absolute inset-0 bg-white opacity-5 pointer-events-none"></div>

          <div className={`relative flex items-center ${isDesktop ? 'max-w-4xl mx-auto px-6 py-7' : 'px-6 py-7'}`}>
            {/* App name with icon */}
            <div className="flex items-center gap-2.5">
              <span className="text-3xl">🐿️</span>
              <h1 className="text-2xl font-bold tracking-tight">Scurry</h1>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center p-8 pb-24" style={{ minHeight: 'calc(100vh - 200px)' }}>
          <div className="text-center max-w-md">
            <div className={'text-6xl mb-4 ' + t.text}>🐿️</div>
            <h2 className="text-2xl font-bold mb-3">Every squirrel needs a plan for winter!</h2>
            <p className={darkMode ? 'text-slate-400 mb-6 font-quicksand' : 'text-slate-600 mb-6 font-quicksand'}>
              Choose a plan that fits your running goals and start collecting acorns. 🌰
            </p>
            <div className="space-y-3">
              <button
                onClick={() => setActiveView('plans')}
                className={getButtonClass({ darkMode, theme: t, variant: 'primary', fullWidth: true }) + ' font-quicksand'}
              >
                Browse Training Plans
              </button>
              <button
                onClick={() => {
                  createCustomPlan();
                  setActiveView('calendar');
                }}
                className={getButtonClass({ darkMode, theme: t, variant: 'secondary', fullWidth: true })}
              >
                Create Custom Plan
              </button>
              <button
                onClick={handleImportClick}
                className={getButtonClass({ darkMode, theme: t, variant: 'secondary', fullWidth: true })}
              >
                Import Plan
              </button>
            </div>
          </div>
        </div>

        {/* Mobile: Navigation at bottom */}
        {!isDesktop && <NavigationBar activeView={activeView} setActiveView={setActiveView} darkMode={darkMode} theme={t} isDesktop={isDesktop} selectedPlan={selectedPlan} />}

        {/* Scout view always mounted (hidden) to process miles and show toasts */}
        <div style={{ display: 'none' }}>
          <ScoutView />
        </div>

        <WelcomeOnboarding
          isOpen={shouldShowWelcome}
          darkMode={darkMode}
          theme={t}
          onClose={dismissWelcome}
          onExplorePlans={handleWelcomeExplore}
          onCreatePlan={handleWelcomeCreate}
        />
      </div>
    );
  }

  return (
    <div className={containerClass}>
      {/* Desktop: Navigation at top */}
      {isDesktop && <NavigationBar activeView={activeView} setActiveView={setActiveView} darkMode={darkMode} theme={t} isDesktop={isDesktop} selectedPlan={selectedPlan} />}

      <div className={'bg-gradient-to-r text-white shadow-lg relative overflow-hidden ' + t.grad}>
        {/* Subtle background pattern */}
        <div className="absolute inset-0 bg-white opacity-5 pointer-events-none"></div>

        <div className={`relative flex items-center justify-between ${isDesktop ? 'max-w-4xl mx-auto px-6 py-7' : 'px-6 py-7'}`}>
          {/* Left: App name with icon */}
          <div className="flex items-center gap-2.5">
            <span className="text-3xl">🐿️</span>
            <h1 className="text-2xl font-bold tracking-tight">Scurry</h1>
          </div>

          {/* Right: Plan info with improved hierarchy */}
          {selectedPlan && (
            <div className="text-right">
              <div className={`font-semibold tracking-wide font-quicksand ${isDesktop ? 'text-base' : 'text-sm max-w-[140px] truncate'}`}>
                {selectedPlan.name}
              </div>
              {selectedPlan.diff && selectedPlan.diff !== 'Custom' && (
                <div className="text-xs opacity-75 uppercase tracking-widest mt-0.5 font-medium">
                  {selectedPlan.diff}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Mobile needs bottom padding for bottom nav, desktop doesn't */}
      <div className={isDesktop ? '' : 'pb-16'}>
        {activeView === 'calendar' && <CalendarView />}
        {activeView === 'plans' && <TrainingPlanSelection />}
        {activeView === 'analytics' && <AnalyticsView />}
        {activeView === 'settings' && <SettingsView />}

        {/* Scout view visible when active */}
        <div style={{ display: activeView === 'scout' ? 'block' : 'none' }}>
          <ScoutView />
        </div>
      </div>

      {/* Mobile: Navigation at bottom */}
      {!isDesktop && <NavigationBar activeView={activeView} setActiveView={setActiveView} darkMode={darkMode} theme={t} isDesktop={isDesktop} selectedPlan={selectedPlan} />}

      {showAddRun && selectedDate && (
        <Modal
          date={selectedDate}
          onAdd={addRun}
          onClose={() => {
            setShowAddRun(false);
            setEditingRun(null);
          }}
          existingRun={editingRun}
          deleteRun={deleteRun}
          darkMode={darkMode}
          types={types}
          t={t}
          distancePresets={distancePresets}
          setDistancePresets={setDistancePresets}
          restDayIcon={restDayIcon}
        />
      )}
      
      {showDuplicateMenu && weekToDuplicate !== null && (
        <DuplicateWeekModal
          darkMode={darkMode}
          theme={t}
          weekToDuplicate={weekToDuplicate}
          duplicateWeek={duplicateWeek}
          setShowDuplicateMenu={setShowDuplicateMenu}
          setWeekToDuplicate={setWeekToDuplicate}
        />
      )}

      {previewPlanData && (
        <SharedPlanPreviewModal
          planData={previewPlanData}
          darkMode={darkMode}
          theme={t}
          onClose={() => setPreviewPlanData(null)}
          onMakeCopy={handleMakeCopy}
          onUseAsTemplate={handleUseAsTemplate}
        />
      )}

      <WelcomeOnboarding
        isOpen={shouldShowWelcome}
        darkMode={darkMode}
        theme={t}
        onClose={dismissWelcome}
        onExplorePlans={handleWelcomeExplore}
        onCreatePlan={handleWelcomeCreate}
      />

      <Toaster
        position="top-center"
        toastOptions={{
          duration: 2000,
          style: {
            background: darkMode ? '#1e293b' : '#ffffff',
            color: darkMode ? '#ffffff' : '#0f172a',
            border: darkMode ? '1px solid #334155' : '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '12px 16px',
            fontSize: '14px',
            fontWeight: '500',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: darkMode ? '#1e293b' : '#ffffff',
            },
          },
        }}
      />
    </div>
  );
};

const App = () => (
  <ErrorBoundary>
    <SettingsProvider>
      <RunPlanProvider>
        <AnalyticsWrapper>
          <RunPlanApp />
        </AnalyticsWrapper>
      </RunPlanProvider>
    </SettingsProvider>
  </ErrorBoundary>
);

// Wrapper to provide Analytics with runs data
const AnalyticsWrapper = ({ children }) => {
  const { runs } = useRunPlan();
  return (
    <AnalyticsProvider runs={runs}>
      {children}
    </AnalyticsProvider>
  );
};

export default App;
